import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Clock, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Logo } from "../components/Logo";
import { QUESTIONS, RESULTS, COUNTRY_CODES } from "../data/quiz";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.4, ease: "easeOut" },
};

const Kicker = ({ children }) => (
  <p className="text-[11px] sm:text-xs font-bold tracking-[0.25em] text-gold uppercase">{children}</p>
);

const OptionCard = ({ letter, text, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`option-${letter.toLowerCase()}`}
    className={`w-full text-left flex items-start gap-4 px-5 py-5 border transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 ${
      selected
        ? "border-gold bg-gold/10"
        : "border-white/15 bg-[#0d0d0d] hover:border-gold/60"
    }`}
  >
    <span
      className={`shrink-0 mt-0.5 h-8 w-8 rounded-full border flex items-center justify-center text-xs font-bold tracking-wider ${
        selected ? "border-gold text-gold" : "border-gold/50 text-gold/70"
      }`}
    >
      {letter}
    </span>
    <span className="text-sm sm:text-[15px] leading-relaxed text-gray-200">{text}</span>
  </button>
);

const GoldButton = ({ children, disabled, onClick, type = "button", testId, className = "" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 px-8 py-3.5 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase transition-[background-color,box-shadow,opacity] duration-300 ${
      disabled
        ? "bg-gold/25 text-black/50 cursor-not-allowed"
        : "bg-gold text-black hover:bg-gold-hover hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.6)]"
    } ${className}`}
  >
    {children}
  </button>
);

const Footer = () => (
  <p className="mt-14 text-center text-[10px] tracking-[0.25em] text-white/30 uppercase">
    CC-ELEVATE.COM &nbsp;&middot;&nbsp; &copy; 2026 CC Elevate
  </p>
);

export default function Quiz() {
  const [step, setStep] = useState("intro"); // intro | 0..6 | form | result
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_prefix: "+39",
    privacy_accepted: false,
    marketing_accepted: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {profile, email_sent}
  const [apiError, setApiError] = useState("");

  const total = QUESTIONS.length;

  const startQuiz = () => setStep(0);

  const selectAnswer = (qid, letter) => {
    setAnswers((prev) => ({ ...prev, [qid]: letter }));
  };

  const goNext = () => {
    if (typeof step === "number") {
      if (step < total - 1) setStep(step + 1);
      else setStep("form");
    }
  };
  const goBack = () => {
    if (typeof step === "number") {
      if (step === 0) setStep("intro");
      else setStep(step - 1);
    }
  };

  const validateForm = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Campo obbligatorio";
    if (!form.last_name.trim()) e.last_name = "Campo obbligatorio";
    if (!form.email.trim()) e.email = "Campo obbligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email non valida";
    if (!form.privacy_accepted) e.privacy = "Devi accettare la Privacy Policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitForm = async (ev) => {
    ev.preventDefault();
    setApiError("");
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        phone_prefix: form.phone_prefix,
        privacy_accepted: form.privacy_accepted,
        marketing_accepted: form.marketing_accepted,
        answers,
      });
      setResult(data);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setApiError(err?.response?.data?.detail || "Si è verificato un errore. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setForm({ first_name: "", last_name: "", email: "", phone: "", phone_prefix: "+39", privacy_accepted: false, marketing_accepted: false });
    setErrors({});
    setResult(null);
    setStep("intro");
  };

  const currentQuestion = typeof step === "number" ? QUESTIONS[step] : null;
  const progress = typeof step === "number" ? Math.round(((step + 1) / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center px-4 py-10 sm:py-14">
      <div className="w-full max-w-2xl flex-1 flex flex-col">
        <Logo className="h-24 sm:h-28 mb-10 sm:mb-14" />

        <AnimatePresence mode="wait">
          {/* ---------- INTRO ---------- */}
          {step === "intro" && (
            <motion.div key="intro" {...fade} className="flex-1">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-12 py-12 sm:py-16 text-center">
                <Kicker>Leadership Assessment</Kicker>
                <p className="mt-6 text-gold text-sm sm:text-base tracking-[0.15em] uppercase font-semibold">
                  Sei pronto per la sfida del 2030?
                </p>
                <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                  Che tipo di <em className="text-gold not-italic font-normal italic">Leader Sei</em>?
                </h1>
                <p className="mt-6 text-gray-400 text-sm sm:text-base leading-relaxed max-w-lg mx-auto">
                  Un assessment di 7 domande per capire il tuo stile di leadership nel contesto del mercato attuale.
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-gold text-xs sm:text-sm font-semibold tracking-wide">
                  <Clock className="h-4 w-4" />
                  <span>5 minuti &middot; 7 domande &middot; Risultato immediato</span>
                </div>
                <div className="mt-9">
                  <GoldButton onClick={startQuiz} testId="start-quiz-btn">
                    Inizia il Quiz Ora <ArrowRight className="h-4 w-4" />
                  </GoldButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* ---------- QUESTIONS ---------- */}
          {currentQuestion && (
            <motion.div key={currentQuestion.id} {...fade} className="flex-1">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-bold tracking-[0.2em] text-gold uppercase" data-testid="progress-label">
                  Domanda {step + 1} di {total}
                </span>
                <span className="text-[11px] sm:text-xs font-bold tracking-[0.1em] text-white/60">{progress}%</span>
              </div>
              <div className="h-[3px] w-full bg-white/10 mb-8">
                <div
                  className="h-full bg-gold transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                  data-testid="progress-bar"
                />
              </div>

              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-5 sm:px-10 py-9 sm:py-11">
                <Kicker>Domanda {step + 1} &middot; {total}</Kicker>
                <h2 className="mt-5 font-serif text-2xl sm:text-3xl leading-snug text-white">
                  {currentQuestion.text}
                </h2>
                <p className="mt-6 text-[11px] tracking-[0.15em] uppercase text-gold/70 font-semibold">
                  * Risposta obbligatoria
                </p>
                <div className="mt-4 grid grid-cols-1 gap-3">
                  {["A", "B", "C"].map((letter) => (
                    <OptionCard
                      key={letter}
                      letter={letter}
                      text={currentQuestion.options[letter]}
                      selected={answers[currentQuestion.id] === letter}
                      onClick={() => selectAnswer(currentQuestion.id, letter)}
                    />
                  ))}
                </div>

                <div className="mt-9 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid="back-btn"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
                  >
                    <ArrowLeft className="h-4 w-4" /> Indietro
                  </button>
                  <GoldButton onClick={goNext} disabled={!answers[currentQuestion.id]} testId="next-btn">
                    Avanti <ArrowRight className="h-4 w-4" />
                  </GoldButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* ---------- LEAD FORM ---------- */}
          {step === "form" && (
            <motion.div key="form" {...fade} className="flex-1">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-10 py-11">
                <Kicker>Ultimo passo</Kicker>
                <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-white">
                  Dove posso <em className="text-gold italic font-normal">inviarti</em> il risultato?
                </h2>
                <p className="mt-4 text-gray-400 text-sm sm:text-base leading-relaxed">
                  Inserisci i tuoi dati per ricevere il tuo profilo di leadership completo.
                </p>

                <form onSubmit={submitForm} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Nome *" error={errors.first_name}>
                    <input
                      data-testid="input-first-name"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Cognome *" error={errors.last_name}>
                    <input
                      data-testid="input-last-name"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Email *" error={errors.email}>
                      <input
                        data-testid="input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Telefono (facoltativo)">
                      <div className="flex gap-3">
                        <select
                          data-testid="select-prefix"
                          value={form.phone_prefix}
                          onChange={(e) => setForm({ ...form, phone_prefix: e.target.value })}
                          className={`${inputCls} w-40 shrink-0 cursor-pointer`}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={c.code} className="bg-[#111]">
                              {c.label}
                            </option>
                          ))}
                        </select>
                        <input
                          data-testid="input-phone"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className={`${inputCls} flex-1`}
                          placeholder="Numero di telefono"
                        />
                      </div>
                    </Field>
                  </div>

                  <div className="sm:col-span-2 space-y-4">
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          data-testid="checkbox-privacy"
                          checked={form.privacy_accepted}
                          onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                          className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                        />
                        <span className="text-xs sm:text-[13px] text-gray-400 leading-relaxed">
                          Ho letto e accetto la{" "}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-gold-hover">
                            Privacy Policy
                          </a>. *
                        </span>
                      </label>
                      {errors.privacy && <p className="mt-2 text-xs text-red-400">{errors.privacy}</p>}
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        data-testid="checkbox-marketing"
                        checked={form.marketing_accepted}
                        onChange={(e) => setForm({ ...form, marketing_accepted: e.target.checked })}
                        className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                      />
                      <span className="text-xs sm:text-[13px] text-gray-400 leading-relaxed">
                        Acconsento a ricevere la newsletter e comunicazioni informative e promozionali di CC Elevate via email. <span className="text-white/40">(facoltativo)</span>
                      </span>
                    </label>
                  </div>

                  {apiError && (
                    <div className="sm:col-span-2 text-sm text-red-400" data-testid="form-error">{apiError}</div>
                  )}

                  <div className="sm:col-span-2 flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => setStep(total - 1)}
                      className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
                    >
                      <ArrowLeft className="h-4 w-4" /> Indietro
                    </button>
                    <GoldButton type="submit" disabled={submitting} testId="submit-lead-btn">
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Invio...
                        </>
                      ) : (
                        <>
                          Scopri il tuo profilo <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </GoldButton>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          {/* ---------- RESULT ---------- */}
          {step === "result" && result && (
            <motion.div key="result" {...fade} className="flex-1" data-testid="result-page">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-12 py-12 sm:py-14">
                <Kicker>Il tuo profilo di leadership</Kicker>
                <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-white leading-tight" data-testid="result-title">
                  {RESULTS[result.profile].title}
                </h1>
                <p className="mt-2 text-gold italic font-serif text-lg sm:text-xl">
                  {RESULTS[result.profile].subtitle}
                </p>
                <div className="mt-6 h-px w-full bg-gradient-to-r from-gold/60 via-gold/20 to-transparent" />
                <p className="mt-6 text-gray-300 text-sm sm:text-base leading-relaxed">
                  {RESULTS[result.profile].body}
                </p>

                <div className="mt-8 space-y-6">
                  {RESULTS[result.profile].sections.map((s) => (
                    <div key={s.label} className="border-l-2 border-gold/60 pl-4">
                      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">{s.label}</p>
                      <p className="mt-1.5 text-gray-300 text-sm sm:text-[15px] leading-relaxed">{s.text}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-9 flex items-center gap-2 text-sm" data-testid="email-status">
                  {result.email_sent ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-gold" />
                      <span className="text-gray-300">Ti abbiamo inviato il profilo completo via email.</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-white/40" />
                      <span className="text-gray-500">Profilo salvato. L'invio email non è al momento attivo.</span>
                    </>
                  )}
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={restart}
                    data-testid="restart-btn"
                    className="text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-gold hover:text-gold-hover transition-colors duration-300"
                  >
                    Rifai il test
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Footer />
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors duration-300";

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-[11px] font-bold tracking-[0.15em] uppercase text-white/60 mb-2">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);
